/**
 * V2Nav — shared sidebar navigation for all /v2/* pages.
 * Single source of truth. Import IconStrip and Sidebar from here.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { detectIPad } from '../../hooks/useIsMobile';
import { useThemeColors } from '../../hooks/useThemeColors';
import { useTheme } from '../../contexts/ThemeContext';

// Helper — derive initials from a name string
export function getInitials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ─── Light-mode palette (kept for backward-compat; components now use useThemeColors) ───
export const L = {
  bgPrimary:   '#F2F0EB',
  bgSecondary: '#E8E5DE',
  bgSurface:   '#FAFAF7',
  bgCode:      '#E0DDD5',
  border:      '#D4D0C8',
  textPrimary: '#1A1916',
  textMuted:   '#7A7669',
  signal:      '#1B4FE0',
  iconStrip:   '#EDEBE4',
};

// ─── Dark-mode iconStrip override (not in useThemeColors palette) ─────────────
const D_iconStrip = '#111318';

// ─────────────────────────────────────────────────────────────────────────────
// Icon strip (collapsed sidebar) — left edge
// ─────────────────────────────────────────────────────────────────────────────



// ─────────────────────────────────────────────────────────────────────────────
// Nav structure — top-level items; Settings has a submenu. (PRI-512: the About
// group was removed — company/marketing content lives on the www site only.)
// ─────────────────────────────────────────────────────────────────────────────

// 'about' retained in the union for legacy callers (PrimateVisionV2, layouts)
// that persist panel state — it now behaves like null (no About panel exists).
type OpenPanel = 'about' | 'settings' | null;

const ICON_VIDEO = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="2.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M11 6.5l4-2.5v8l-4-2.5V6.5z" fill="currentColor" opacity="0.6"/>
  </svg>
);
/* PRI-310: ICON_DOCS parked while the API Docs nav is hidden. Restore with the nav item.
const ICON_DOCS = (
  <svg width="14" height="15" viewBox="0 0 14 16" fill="none">
    <rect x="1.5" y="1" width="11" height="14" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
    <path d="M4 5h6M4 8h6M4 11h4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
  </svg>
);
*/
// PRI-512: ICON_ABOUT removed with the About nav group.
// PRI-523: ICON_PRICING removed — canonical pricing lives on www; the app's
// purchase surface is User Settings → Billing/Usage.
const ICON_SETTINGS = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path d="M6.5 1h3l.5 1.8a5 5 0 0 1 1.2.7l1.8-.6 1.5 2.6-1.4 1.2a5 5 0 0 1 0 1.6l1.4 1.2-1.5 2.6-1.8-.6a5 5 0 0 1-1.2.7L9.5 15h-3l-.5-1.8a5 5 0 0 1-1.2-.7l-1.8.6-1.5-2.6 1.4-1.2a5 5 0 0 1 0-1.6L1.5 6.4 3 3.8l1.8.6a5 5 0 0 1 1.2-.7L6.5 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2"/>
  </svg>
);

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai'];
export function checkIsAdmin(email?: string | null): boolean {
  return !!email && ADMIN_EMAILS.includes(email);
}

// User account items live in the account menu (bottom row), not the nav sidebar
// PRI-310: API Keys hidden during friendly-tester phase. Web SDK / API access is
// future-EPIC scope; the route still exists for admin debugging but is not
// linked from any user-visible nav.
const ACCOUNT_MENU_ITEMS = [
  { label: 'Usage',    href: '/dashboard/usage',    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg> },
  { label: 'Billing',  href: '/dashboard/billing',  icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
  { label: 'Settings', href: '/dashboard/settings', icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6.5 1h3l.5 1.8a5 5 0 0 1 1.2.7l1.8-.6 1.5 2.6-1.4 1.2a5 5 0 0 1 0 1.6l1.4 1.2-1.5 2.6-1.8-.6a5 5 0 0 1-1.2.7L9.5 15h-3l-.5-1.8a5 5 0 0 1-1.2-.7l-1.8.6-1.5-2.6 1.4-1.2a5 5 0 0 1 0-1.6L1.5 6.4 3 3.8l1.8.6a5 5 0 0 1 1.2-.7L6.5 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2"/></svg> },
];

const SETTINGS_ITEMS_ADMIN = [
  { label: 'Waitlist',       href: '/dashboard/admin',            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg> },
  { label: 'Email Queue',    href: '/dashboard/admin/emails',     icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg> },
  { label: 'Customer CRM',   href: '/dashboard/leads',            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg> },
  { label: 'Design Partners', href: '/dashboard/admin/design-partners', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg> },
  { label: 'Fundraise',      href: '/dashboard/investors',        icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg> },
  { label: 'Investor CRM',   href: '/dashboard/investor-crm',    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg> },
  { label: 'Growth',         href: '/dashboard/growth',           icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg> },
  { label: 'Win/Loss',       href: '/dashboard/admin/winloss',   icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M8 21h8M12 17v4M17 5h2a2 2 0 010 4h-2"/><path d="M7 5H5a2 2 0 000 4h2"/><path d="M7 5a5 5 0 0010 0V3H7v2z"/></svg> },
  { label: 'API Quotas',     href: '/dashboard/admin/pricing',   icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6.5 1h3l.5 1.8a5 5 0 0 1 1.2.7l1.8-.6 1.5 2.6-1.4 1.2a5 5 0 0 1 0 1.6l1.4 1.2-1.5 2.6-1.8-.6a5 5 0 0 1-1.2.7L9.5 15h-3l-.5-1.8a5 5 0 0 1-1.2-.7l-1.8.6-1.5-2.6 1.4-1.2a5 5 0 0 1 0-1.6L1.5 6.4 3 3.8l1.8.6a5 5 0 0 1 1.2-.7L6.5 1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2"/></svg> },
  { label: 'DevOps',         href: '/dashboard/admin/devops',    icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/><path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg> },
  { label: 'Demo Library',   href: '/dashboard/admin/demo-library', icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="2" width="14" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="7" width="14" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="12" width="9" height="2" rx="1" fill="currentColor" opacity="0.6"/></svg> },
  { label: 'API Settings',   href: '/dashboard/admin/api-settings', icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="7" width="9" height="2" rx="1" fill="currentColor" opacity="0.6"/><rect x="1" y="11" width="11" height="2" rx="1" fill="currentColor" opacity="0.6"/></svg> },
  { label: 'Blog Admin',     href: '/dashboard/blog',             icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg> },
  { label: 'Careers Admin',  href: '/dashboard/careers',          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> },
  { label: 'Prompts',        href: '/dashboard/admin/prompts',   icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/></svg> },
];

// ─────────────────────────────────────────────────────────────────────────────
// Icon Strip — 4 icons, clicking About/Settings opens sidebar pre-expanded
// ─────────────────────────────────────────────────────────────────────────────

function IconStrip({ onOpen, isSignedIn, isAdmin: _isAdmin = false, userInitials = '?' }: { onOpen: (panel: OpenPanel) => void; isSignedIn: boolean; isAdmin?: boolean; userInitials?: string }) {
  const [hovered, setHovered] = useState<string | null>(null);
  const c = useThemeColors();
  const { resolvedTheme } = useTheme();
  const iconStripBg = resolvedTheme === 'light' ? L.iconStrip : D_iconStrip;
  // iPad gets Apple-HIG-compliant 44×44pt touch targets and a slightly wider strip
  const isIPad = useMemo(() => detectIPad(), []);

  const items = [
    { id: 'videos',   icon: ICON_VIDEO,    label: 'Video Library', action: () => onOpen(null) },
    // PRI-310: API Docs hidden during friendly-tester phase.
    // { id: 'docs',     icon: ICON_DOCS,     label: 'API Docs',      action: () => { window.location.href = '/docs'; } },
    // PRI-512: About group removed. PRI-523: Pricing entry removed — pricing
    // is linked only from the www marketing site.
    // Settings only shown for admins — non-admins use account menu for user settings
    ...(_isAdmin ? [{ id: 'settings', icon: ICON_SETTINGS, label: 'Settings', action: () => onOpen('settings') }] : []),
  ];

  const stripW = isIPad ? 56 : 48;
  const btnSize = isIPad ? 44 : 36;

  return (
    <div data-testid="icon-strip" style={{
      position: 'fixed', top: 0, left: 0, bottom: 0,
      width: stripW, zIndex: 100,
      background: iconStripBg,
      borderRight: `1px solid ${c.border}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      paddingTop: 10, paddingBottom: 12, gap: 2,
    }}>
      {/* Sidebar toggle at very top — two-panel icon (à la Claude.ai) */}
      <button
        title="Toggle sidebar"
        onClick={() => onOpen(null)}
        onMouseEnter={() => setHovered('menu')}
        onMouseLeave={() => setHovered(null)}
        style={{
          width: btnSize, height: btnSize, borderRadius: 8, marginBottom: 6,
          background: hovered === 'menu' ? c.border : 'transparent',
          border: 'none', cursor: 'pointer', color: c.textMuted,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background 120ms, color 120ms',
        }}
      >
        {/* Two-panel sidebar icon: narrow left column + wider right area with lines */}
        <svg width="16" height="14" viewBox="0 0 16 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.5" y="0.5" width="15" height="13" rx="2" stroke="currentColor" strokeWidth="1.2"/>
          <line x1="5" y1="0.5" x2="5" y2="13.5" stroke="currentColor" strokeWidth="1.2"/>
          <line x1="8" y1="4" x2="13" y2="4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
          <line x1="8" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
          <line x1="8" y1="10" x2="11" y2="10" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
        </svg>
      </button>

      {items.map(item => {
        const isH = hovered === item.id;
        return (
          <button
            key={item.id}
            title={item.label}
            onClick={item.action}
            onMouseEnter={() => setHovered(item.id)}
            onMouseLeave={() => setHovered(null)}
            style={{
              width: btnSize, height: btnSize, borderRadius: 8,
              background: isH ? c.border : 'transparent',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: isH ? c.textPrimary : c.textMuted,
              transition: 'background 120ms, color 120ms',
            }}
          >
            {item.icon}
          </button>
        );
      })}

      <div style={{ flex: 1 }} />

      {/* Account avatar at bottom */}
      <button
        onClick={() => onOpen('settings')}
        title={isSignedIn ? 'Account' : 'Sign in'}
        onMouseEnter={() => setHovered('account')}
        onMouseLeave={() => setHovered(null)}
        style={{
          width: btnSize, height: btnSize, borderRadius: '50%',
          background: isSignedIn ? c.signal : c.bgCode,
          border: isSignedIn ? 'none' : `1.5px solid ${c.border}`,
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          outline: hovered === 'account' ? `2px solid ${c.signal}` : 'none',
          outlineOffset: 2,
          transition: 'outline 120ms',
          flexShrink: 0,
        }}
      >
        {isSignedIn
          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', letterSpacing: '-0.02em' }}>{userInitials}</span>
          : <img src="/Darwin_Front_v1_headshot.jpg" alt="Darwin" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
        }
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar (open state) — nav items with expandable submenus
// ─────────────────────────────────────────────────────────────────────────────

// Minimal video shape for sidebar display — compatible with both UserVideo and DemoVideoLibraryEntry
export interface SidebarVideo {
  id: string;
  display_name: string;
}

// ── Sidebar collapsible-group persistence ──────────────────────────────────────
// Read a persisted boolean from localStorage ('1'/'0'). Returns `fallback` when
// there is no stored value or storage is unavailable (SSR/private mode).
export function readPersistedSidebar(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}

// Write a boolean to localStorage as '1'/'0'. No-ops if storage is unavailable.
export function writePersistedSidebar(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? '1' : '0');
  } catch { /* SSR/private-mode safe */ }
}

function Sidebar({ open, onClose, isSignedIn, isAdmin = false, initialPanel, userInitials = '?', userName, isPro = false, planTier, demoVideos = [], userVideos = [], streamSessionCount, onLiveStreamsSelect, onLiveStreamsDelete, onLiveStreamsNewSession, onSignOut, onUserVideoSelect, onDemoVideoSelect, onUserVideoRename, onUserVideoDelete, onDemoVideoDeleteSession, isMobile = false }: {
  open: boolean;
  onClose: () => void;
  isSignedIn: boolean;
  isAdmin?: boolean;
  initialPanel: OpenPanel;
  userInitials?: string;
  userName?: string | null;
  isPro?: boolean;
  planTier?: string;
  onSignOut?: () => void;
  demoVideos?: SidebarVideo[];
  userVideos?: SidebarVideo[];
  streamSessionCount?: number;
  onLiveStreamsSelect?: () => void;
  onLiveStreamsDelete?: () => void;
  onLiveStreamsNewSession?: () => void;
  onUserVideoSelect?: (id: string) => void;
  onDemoVideoSelect?: (id: string) => void;
  onUserVideoRename?: (id: string, name: string) => void;
  onUserVideoDelete?: (id: string) => void;
  onDemoVideoDeleteSession?: (id: string) => void;
  isMobile?: boolean;
}) {
  // Seed collapsible-group state from localStorage so expand/collapse persists
  // across reloads/navigation; fall back to the initialPanel-derived default
  // only when there is no stored value. (SSR/private-mode safe via try/catch.)
  // PRI-512: aboutOpen state removed with the About group.
  const [settingsOpen, setSettingsOpen] = useState(() => readPersistedSidebar('pi.sidebar.settingsOpen', initialPanel === 'settings'));
  const [videosOpen, setVideosOpen] = useState(() => readPersistedSidebar('pi.sidebar.videosOpen', initialPanel === null)); // open by default
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const c = useThemeColors();

  const DEMO_VIDEOS = demoVideos;
  const USER_VIDEOS = isSignedIn ? userVideos : [];

  // When panel changes from outside (strip click), update expanded state.
  // Explicit strip clicks are an intentional user action and still win at
  // runtime; the write-back effects below then persist the new state.
  // We skip the first run so the localStorage-seeded initial state above is
  // preserved on mount (only genuine initialPanel changes override it).
  const didMountPanelEffect = useRef(false);
  useEffect(() => {
    if (!didMountPanelEffect.current) { didMountPanelEffect.current = true; return; }
    if (initialPanel === 'about') { setVideosOpen(true); } // legacy value — About removed (PRI-512)
    if (initialPanel === 'settings') { setSettingsOpen(true); setVideosOpen(false); }
    if (initialPanel === null) { setVideosOpen(true); }
  }, [initialPanel]);

  // Persist each collapsible group's open/closed state to localStorage.
  useEffect(() => { writePersistedSidebar('pi.sidebar.settingsOpen', settingsOpen); }, [settingsOpen]);
  useEffect(() => { writePersistedSidebar('pi.sidebar.videosOpen', videosOpen); }, [videosOpen]);

  const adminItems = isAdmin ? SETTINGS_ITEMS_ADMIN : [];
  const swipeTouchStartX = useRef(0);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      pointerEvents: open ? 'auto' : 'none',
    }}>
      {open && (
        <div onClick={onClose} style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.25)',
          backdropFilter: 'blur(2px)',
        }} />
      )}
      <div
        onTouchStart={(e) => { swipeTouchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (e.changedTouches[0].clientX - swipeTouchStartX.current > 60) onClose();
        }}
        style={{
        position: 'absolute', top: 0, left: 0, bottom: 0,
        width: 260,
        background: c.bgSecondary,
        borderRight: `1px solid ${c.border}`,
        display: 'flex', flexDirection: 'column',
        paddingBottom: isMobile ? 'calc(49px + env(safe-area-inset-bottom))' : 0,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Header — logo + close */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 10px 8px 12px',
          height: 104,
          borderBottom: `1px solid ${c.border}`,
          flexShrink: 0,
          gap: 0,
        }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0, textDecoration: 'none' }}>
            <img src="/darwin-v3-transparent.png" alt="Primate Vision" style={{ height: 88, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.15 }}>
              <span style={{ fontSize: 23, fontWeight: 300, color: c.textPrimary, letterSpacing: '-0.025em', whiteSpace: 'nowrap' }}>Primate</span>
              <span style={{ fontSize: 23, fontWeight: 300, color: c.signal, letterSpacing: '-0.025em', whiteSpace: 'nowrap' }}>Vision</span>
            </div>
          </a>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, padding: 4, borderRadius: 4, display: 'flex', alignSelf: 'flex-start', marginTop: 6, flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M1 1l11 11M12 1L1 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Scrollable nav */}
        <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 16 }}>
          {/* Video Library — expandable list */}
          <NavItem
            icon={ICON_VIDEO}
            label="Video Library"
            expanded={videosOpen}
            onToggle={() => setVideosOpen(o => !o)}
            hasChildren
          />
          {videosOpen && (
            <div style={{ paddingLeft: 14 }}>
              {/* Live Streams — always shown so user can double-click to start a new session */}
              <LiveStreamsSubItem
                sessionCount={streamSessionCount ?? 0}
                onSelect={onLiveStreamsSelect}
                onDelete={onLiveStreamsDelete}
                onNewSession={onLiveStreamsNewSession}
              />
              {/* User Videos subheading */}
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, padding: '8px 16px 4px', fontWeight: 600 }}>Local File Sessions</div>
              {USER_VIDEOS.length > 0 ? (
                USER_VIDEOS.map(v => (
                  <UserVideoSubItem
                    key={v.id}
                    label={v.display_name}
                    id={v.id}
                    onSelect={() => onUserVideoSelect?.(v.id)}
                    onRename={onUserVideoRename}
                    onDelete={onUserVideoDelete}
                  />
                ))
              ) : (
                <div style={{ padding: '4px 16px 8px', fontSize: 12, color: c.textMuted, lineHeight: 1.6 }}>
                  {isSignedIn
                    ? 'No saved videos yet.'
                    : <><a href="/signup" style={{ color: c.signal, textDecoration: 'none' }}>Create a free account</a> to run more analyses and save results.</>}
                </div>
              )}
              {/* Demo Videos — always shown; API populates entries when available */}
              <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, padding: '8px 16px 4px', fontWeight: 600 }}>Demo Video Sessions</div>
              {DEMO_VIDEOS.length > 0
                ? DEMO_VIDEOS.map(v => (
                    <DemoVideoSubItem
                      key={v.id}
                      label={v.display_name}
                      id={v.id}
                      onSelect={() => onDemoVideoSelect?.(v.id)}
                      onDeleteSession={onDemoVideoDeleteSession}
                    />
                  ))
                : null
              }
            </div>
          )}

          {/* PRI-310: API Docs nav hidden during friendly-tester phase.
              Restore when Web SDK EPIC ships. */}
          {/* {!isMobile && <NavItem icon={ICON_DOCS} label="API Docs" href="/docs" />} */}

          {/* PRI-512: About group removed. PRI-523: Pricing link removed —
              canonical pricing lives on the www marketing site. */}

          {/* Settings — only shown for admins; non-admins find user settings in account menu */}
          {isAdmin && (
            <NavItem
              icon={ICON_SETTINGS}
              label="Admin Settings"
              expanded={settingsOpen}
              onToggle={() => setSettingsOpen(o => !o)}
              hasChildren
            />
          )}
          {settingsOpen && isAdmin && (
            <div style={{ paddingLeft: 14 }}>
              {adminItems.map(i => (
                <SubItem key={i.href} label={i.label} href={i.href} icon={i.icon} />
              ))}
            </div>
          )}
        </div>

        {/* Bottom account row */}
        <div style={{ borderTop: `1px solid ${c.border}`, flexShrink: 0, position: 'relative' }}>
          {accountMenuOpen && (
            <>
              <div onClick={() => setAccountMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 299 }} />
              <div style={{
                position: 'absolute', bottom: 'calc(100% + 6px)', left: 12, right: 12,
                background: c.bgSurface, border: `1px solid ${c.border}`,
                borderRadius: 10, padding: 6,
                boxShadow: '0 6px 20px rgba(0,0,0,0.1)', zIndex: 300,
              }}>
                {isSignedIn ? (
                  <>
                    {ACCOUNT_MENU_ITEMS.map(item => (
                      <a key={item.href} href={item.href} style={{ display: 'block', padding: '8px 12px', borderRadius: 7, fontSize: 13, color: c.textPrimary, textDecoration: 'none' }}
                        onMouseEnter={e => (e.currentTarget.style.background = `${c.signal}0f`)}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >{item.label}</a>
                    ))}
                    <div style={{ height: 1, background: c.border, margin: '4px 0' }} />
                    <button onClick={onSignOut} style={{ display: 'block', width: '100%', padding: '8px 12px', borderRadius: 7, fontSize: 13, color: c.textMuted, background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' as const }}>Sign out</button>
                  </>
                ) : (
                  <>
                    <a href="/login" style={{ display: 'block', padding: '8px 12px', borderRadius: 7, fontSize: 13, color: c.signal, textDecoration: 'none' }}>Log in</a>
                    <a href="/signup" style={{ display: 'block', padding: '8px 12px', borderRadius: 7, fontSize: 13, color: c.textPrimary, textDecoration: 'none', fontWeight: 500 }}>Create free account</a>
                  </>
                )}
              </div>
            </>
          )}
          <button onClick={() => setAccountMenuOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' as const }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: isSignedIn ? c.signal : c.bgCode, border: isSignedIn ? 'none' : `1.5px solid ${c.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {isSignedIn ? <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{userInitials}</span> : <img src="/Darwin_Front_v1_headshot.jpg" alt="Darwin" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {isSignedIn
                ? <><div style={{ fontSize: 13, fontWeight: 500, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName ?? 'Account'}</div><div style={{ fontSize: 11, color: c.textMuted }}>{isAdmin ? 'Administrator' : (planTier ? `${planTier.charAt(0).toUpperCase()}${planTier.slice(1)} plan` : (isPro ? 'Pro plan' : 'Free plan'))}</div></>
                : <div style={{ fontSize: 13, color: c.textMuted }}>Anonymous</div>
              }
            </div>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, color: c.textMuted, transform: accountMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
              <path d="M1.5 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// Nav item (top-level row with icon + label)
function NavItem({ icon, label, href, onClick, hasChildren, expanded, onToggle }: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
  hasChildren?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const [h, setH] = useState(false);
  const c = useThemeColors();
  const style: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10,
    width: '100%', padding: '9px 16px',
    background: h ? `${c.signal}0d` : 'transparent',
    border: 'none', cursor: 'pointer', textAlign: 'left',
    transition: 'background 120ms',
    textDecoration: 'none',
    color: h ? c.textPrimary : c.textMuted,
  };
  const content = (
    <>
      <span style={{ flexShrink: 0, display: 'flex', color: 'inherit' }}>{icon}</span>
      <span style={{ fontSize: 13, color: 'inherit', flex: 1 }}>{label}</span>
      {hasChildren && (
        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
          <path d="M1.5 3.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        </svg>
      )}
    </>
  );
  if (hasChildren) {
    return (
      <button onClick={onToggle} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={style}>
        {content}
      </button>
    );
  }
  if (href) {
    return <a href={href} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={style}>{content}</a>;
  }
  return <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={style}>{content}</button>;
}

// Submenu item (indented, smaller)
function SubItem({ label, href, icon, onSelect }: { label: string; href: string; icon?: React.ReactNode; onSelect?: () => void }) {
  const [h, setH] = useState(false);
  const c = useThemeColors();
  return (
    <a
      href={href}
      onClick={onSelect ? (e) => { e.preventDefault(); onSelect(); } : undefined}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '7px 16px',
        fontSize: 12, color: h ? c.textPrimary : c.textMuted,
        textDecoration: 'none', background: h ? `${c.signal}0d` : 'transparent',
        transition: 'all 120ms',
      }}
    >
      {icon && <span style={{ flexShrink: 0, display: 'flex', color: 'inherit', opacity: 0.7 }}>{icon}</span>}
      {label}
    </a>
  );
}

// Trash icon
const ICON_TRASH = (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M1 3h10M4 3V2h4v1M2 3l.8 7.2A1 1 0 003.8 11h4.4a1 1 0 001-.8L10 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Pencil icon
const ICON_PENCIL = (
  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
    <path d="M8 2l2 2L4 10H2V8L8 2z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// User video submenu item — hover shows pencil (rename) + trash (delete)
function UserVideoSubItem({ label, id, onSelect, onRename, onDelete }: {
  label: string;
  id: string;
  onSelect: () => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
}) {
  const [h, setH] = useState(false);
  const [editing, setEditing] = useState(false);
  const c = useThemeColors();
  const [editVal, setEditVal] = useState(label);

  const commitRename = () => {
    const trimmed = editVal.trim();
    if (trimmed && trimmed !== label) onRename?.(id, trimmed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div style={{ padding: '4px 16px' }}>
        <input
          autoFocus
          value={editVal}
          onChange={e => setEditVal(e.target.value)}
          onBlur={commitRename}
          onKeyDown={e => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') { setEditVal(label); setEditing(false); }
          }}
          style={{
            width: '100%', fontSize: 12, color: c.textPrimary,
            background: c.bgSurface, border: `1px solid ${c.signal}`,
            borderRadius: 4, padding: '3px 6px', outline: 'none',
            fontFamily: 'Inter, -apple-system, sans-serif',
          }}
        />
      </div>
    );
  }

  return (
    <div
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'flex', alignItems: 'center',
        background: h ? `${c.signal}0d` : 'transparent',
        transition: 'background 120ms',
      }}
    >
      <a
        href="#"
        onClick={e => e.preventDefault()}
        onDoubleClick={e => { e.preventDefault(); onSelect(); }}
        title="Double-click to load video"
        style={{
          flex: 1, display: 'flex', alignItems: 'center',
          padding: '7px 8px 7px 16px',
          fontSize: 12, color: h ? c.textPrimary : c.textMuted,
          textDecoration: 'none',
          userSelect: 'none',
        }}
      >
        {label}
      </a>
      {h && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, paddingRight: 8 }}>
          {onRename && (
            <button
              title="Rename"
              onClick={e => { e.stopPropagation(); setEditing(true); setEditVal(label); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, padding: '2px 4px', display: 'flex', alignItems: 'center', borderRadius: 3 }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = c.textPrimary; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted; }}
            >
              {ICON_PENCIL}
            </button>
          )}
          {onDelete && (
            <button
              title="Delete video"
              onClick={e => { e.stopPropagation(); if (window.confirm('Delete this video and all its session data?')) onDelete(id); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, padding: '2px 4px', display: 'flex', alignItems: 'center', borderRadius: 3 }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e53e3e'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted; }}
            >
              {ICON_TRASH}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Live Streams submenu item:
// - Always shown under Video Library
// - Single-click (with history): loads all stream session results
// - Double-click (no history): starts a new live stream session
// - Hover: shows trash icon to delete all session history (entry stays)
function LiveStreamsSubItem({ sessionCount, onSelect, onDelete, onNewSession }: {
  sessionCount: number;
  onSelect?: () => void;
  onDelete?: () => void;
  onNewSession?: () => void;
}) {
  const [h, setH] = useState(false);
  const lastClickRef = useRef<number>(0);
  const c = useThemeColors();

  const handleClick = () => {
    const now = Date.now();
    const delta = now - lastClickRef.current;
    lastClickRef.current = now;

    if (delta < 400) {
      // Double-click: always start a new session (with or without history)
      onNewSession?.();
      return;
    }
    // Single-click: load history if it exists
    if (sessionCount > 0) {
      onSelect?.();
    }
  };

  return (
    <>
      <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase' as const, color: c.textMuted, padding: '8px 16px 4px', fontWeight: 600 }}>Live Camera Sessions</div>
      <div
        onMouseEnter={() => setH(true)}
        onMouseLeave={() => setH(false)}
        style={{
          display: 'flex', alignItems: 'center',
          background: h ? `${c.signal}0d` : 'transparent',
          transition: 'background 120ms',
        }}
      >
        <button
          onClick={handleClick}
          title={sessionCount === 0 ? 'Double-click to start a live stream' : 'Click to view history · Double-click to start new stream'}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 8px 6px 16px',
            background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' as const,
            fontSize: 13,
            color: sessionCount > 0 ? c.textPrimary : c.textMuted,
            transition: 'color 120ms',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2"/>
            <circle cx="6" cy="6" r="2" fill="currentColor" opacity={sessionCount > 0 ? 0.6 : 0.3}/>
          </svg>
          <span style={{ flex: 1 }}>
            {sessionCount > 0 ? `All sessions (${sessionCount})` : 'All sessions'}
          </span>
        </button>
        {h && sessionCount > 0 && onDelete && (
          <button
            title="Delete all live stream session history"
            onClick={e => {
              e.stopPropagation();
              if (window.confirm('Delete all live stream session history? The Live Streams entry will remain.')) {
                onDelete();
              }
            }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, padding: '2px 4px', marginRight: 8, display: 'flex', alignItems: 'center', borderRadius: 3 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e53e3e'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted; }}
          >
            {ICON_TRASH}
          </button>
        )}
      </div>
    </>
  );
}

// Demo video submenu item — hover shows only trash (deletes session data, not the video)
function DemoVideoSubItem({ label, id, onSelect, onDeleteSession }: {
  label: string;
  id: string;
  onSelect: () => void;
  onDeleteSession?: (id: string) => void;
}) {
  const [h, setH] = useState(false);
  const c = useThemeColors();
  return (
    <div
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        display: 'flex', alignItems: 'center',
        background: h ? `${c.signal}0d` : 'transparent',
        transition: 'background 120ms',
      }}
    >
      <a
        href="#"
        onClick={e => { e.preventDefault(); onSelect(); }}
        style={{
          flex: 1, display: 'flex', alignItems: 'center',
          padding: '7px 8px 7px 16px',
          fontSize: 12, color: h ? c.textPrimary : c.textMuted,
          textDecoration: 'none',
        }}
      >
        {label}
      </a>
      {h && onDeleteSession && (
        <button
          title="Clear session data"
          onClick={e => { e.stopPropagation(); if (window.confirm('Clear all session results for this demo video?')) onDeleteSession(id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.textMuted, padding: '2px 4px', marginRight: 8, display: 'flex', alignItems: 'center', borderRadius: 3 }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#e53e3e'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = c.textMuted; }}
        >
          {ICON_TRASH}
        </button>
      )}
    </div>
  );
}



export type { OpenPanel };
export { IconStrip, Sidebar };
